import "@fontsource-variable/archivo-narrow"

/**
 * La letra del carnet: Archivo Narrow, empaquetada con el sistema.
 *
 * ⚠ **Se pidió «tipo Aptos Narrow», y Aptos Narrow NO se puede usar tal cual.** Es de
 * Microsoft y llega con Office: no se puede servir desde nuestro origen, y nombrarla
 * sin más en el CSS haría que el carnet saliera con ella en los equipos que tienen
 * Office y con otra en los que no — con otro ancho, así que el nombre que cabe en uno
 * se parte en el otro. Archivo Narrow es libre (OFL), del mismo corte —una grotesca
 * estrecha— y viaja en el bundle: sale igual en cualquier equipo, con o sin internet.
 * El nombre de la familia está en `--font-carnet`, en `globales.css`.
 *
 * ⚠ **Estrecha a propósito, y no por gusto.** En los 46 mm del nombre, a 4 mm de
 * cuerpo, Montserrat Bold mete catorce versales y ésta dieciocho: el mismo nombre
 * cabe sin encoger.
 *
 * ⚠ **No trae cifras tabulares, y se midió**: con `tabular-nums`, diez unos miden 402
 * y diez ceros 456 — lo mismo que sin él. En una tarjeta no hay columna que alinear,
 * así que las cifras del carnet van proporcionales y sin la clase `tabular`, que aquí
 * no haría nada.
 */

/**
 * Ancho de una versal de Archivo Narrow Bold, en fracciones del cuerpo.
 *
 * ⚠ **Medido, no supuesto** (Edge, peso 700): la media de la A a la Z es 0,564, y la
 * palabra más ancha de una muestra de nombres y cargos —la de las emes y la uve
 * doble— da 0,625 por letra. Se toma la segunda, redondeada hacia arriba: con la
 * media, un apellido con dos emes se sale de la tarjeta. El 0,78 de `cuerpoQueCabe`
 * es de Montserrat, y aquí dejaría los nombres un 20 % más pequeños de lo que caben.
 */
export const ANCHO_VERSAL = 0.63

/**
 * Ancho medio de una minúscula de Archivo Narrow SemiBold, en fracciones del cuerpo. Lo
 * usa el correo, que es lo único del carnet en minúsculas que tiene que caber entero.
 *
 * ⚠ **Medido** (Edge, peso 600): la media de la a a la z es 0,429, y la muestra real
 * más ancha —«operaciones@», por la arroba, que mide 0,81 ella sola— da 0,461. Se toma
 * 0,47. Un correo de puras emes y uves dobles (0,68) se saldría, y ninguno lo es.
 */
export const ANCHO_MINUSCULA = 0.47

/**
 * El cuerpo, en mm, al que `texto` —en versales— cabe en UNA línea de `ancho` mm.
 *
 * ⚠ **Mide el texto entero, no la palabra más larga**, que es lo que hace
 * `cuerpoQueCabe` para decidir dónde envolver. En el carnet cada línea tiene que
 * caber entera: el nombre va en dos líneas fijas, y envolverlas daría tres o cuatro
 * y empujaría el código de barras fuera del corte. Por debajo del `minimo` no
 * se encoge más: a partir de ahí se envuelve, por palabras.
 */
export function cuerpoEnUnaLinea(
  texto: string,
  ancho: number,
  { tope, minimo, espaciado = 0 }: { tope: number; minimo: number; espaciado?: number },
): number {
  const letras = Math.max(1, texto.trim().length)
  const cuerpo = (ancho - espaciado * (letras - 1)) / (letras * ANCHO_VERSAL)
  return Math.max(minimo, Math.min(tope, cuerpo))
}
